import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {EQueryTab} from '../../e-queries/models/e-query-tab.enum';
import {FilterFieldValue} from '../../covance-filter';
import {SponsorEQuery} from './models/sponsor-e-query';
import {map, switchMap, tap, startWith} from 'rxjs/operators';
import {deserialize} from 'santee-dcts';
import {Settings} from '../../core';
import {HttpClient} from '@angular/common/http';
import {ExternalUserFilterUrlParamsPreparer} from '../../e-queries/external-user-filter-url-params-preparer';
import {SponsorEQueryFilterItemsReadModel} from './models/sponsor-e-queries-filter-items-read-model';
import {SponsorEQueriesReadModel} from './models/sponsor-e-queries-read-model';
import {SponsorEQueryFilterItem} from './models/sponsor-e-query-filter-item';
import {ExternalUserEQueriesStorage} from '../../e-queries/external-user-e-queries-storage';
import {SponsorEQueriesCountersStorage} from './sponsor-e-queries-counters-storage';

@Injectable()
export class SponsorEQueriesStorage implements ExternalUserEQueriesStorage<SponsorEQuery> {
    private readonly refresher = new BehaviorSubject<void>(undefined);

    constructor(private readonly settings: Settings,
                private readonly http: HttpClient,
                private readonly externalFilterUrlParamsPreparer: ExternalUserFilterUrlParamsPreparer,
                private readonly sponsorEQueriesCountersStorage: SponsorEQueriesCountersStorage) { }

    public getEQueryFilterItems(data$: Observable<[number, EQueryTab]>): Observable<SponsorEQueryFilterItem[]> {
        return combineLatest([
            data$,
            this.refresher,
        ]).pipe(
            switchMap(([[siteId, tab]]) => {
                return this.http.get(
                    `${this.settings.apiUrl}/sponsor/site/${siteId}/e-query-filter-items/${tab}`
                ).pipe(
                    map((r) => deserialize(r, SponsorEQueryFilterItemsReadModel).filterItems),
                );
            })
        );
    }

    public getEQueries(siteId: number, tab: EQueryTab, filterFields: FilterFieldValue[]): Observable<SponsorEQuery[]> {
        const params = this.externalFilterUrlParamsPreparer.prepareFilterUrlParams(filterFields);

        return this.refresher.pipe(
            switchMap(() =>
                this.http.get(
                    `${this.settings.apiUrl}/sponsor/site/${siteId}/e-queries/${tab}`, {params: params}
                ).pipe(
                    map((r) => deserialize(r, SponsorEQueriesReadModel).eQueries),
                    tap(eQueries => {
                        const firstEQuery = eQueries[0];

                        if (!firstEQuery) {
                            return;
                        }

                        this.sponsorEQueriesCountersStorage.updateCounters(
                            firstEQuery.siteId,
                            eQueries,
                            eq => eq.eQueryId,
                            eq => eq.isUnresolved);
                    }),
                    startWith(null),
                )
            ),
        );
    }

    public forceLoadEQueries() {
        this.refresher.next();
    }
}
